import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import apiClient from '../../services/apiClient';

interface EvalData {
  summary: {
    total_trades: number;
    open_trades: number;
    pending_trades: number;
    cancelled_trades: number;
    wins: number;
    losses: number;
    win_rate: number | null;
    total_pnl: number;
    avg_pnl: number;
    best_trade: number;
    worst_trade: number;
  };
  score_buckets: Record<string, { count: number; win_rate: number | null }>;
  open_positions: any[];
  pending_positions: any[];
  cancelled_positions: any[];
  recent_closed: any[];
}

const pnlColor = (v: number) => (v >= 0 ? 'success.main' : 'error.main');
const pnlStr = (v: number) => `${v >= 0 ? '+' : ''}$${v.toFixed(0)}`;
const wrColor = (wr: number | null) => {
  if (wr === null) return 'text.secondary';
  if (wr >= 60) return 'success.main';
  if (wr < 40) return 'error.main';
  return 'warning.main';
};
const fmtTs = (ts: string | null) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};
const fmtDate = (ts: string | null) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const legsSummary = (legs: any) => {
  if (!legs) return '';
  const arr = typeof legs === 'string' ? JSON.parse(legs) : legs;
  if (!Array.isArray(arr)) return '';
  return arr.map((l: any) => `${l.side === 'buy' ? '+' : '-'}$${l.strike}${l.option_type}`).join(' / ');
};
const spotMove = (entry: string | null, live: string | null) => {
  if (!entry || !live) return null;
  const e = parseFloat(entry), l = parseFloat(live);
  if (!e) return null;
  return ((l - e) / e * 100);
};

const PulseEval: React.FC = () => {
  const [data, setData] = useState<EvalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [tickerFilter, setTickerFilter] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');  // win/loss/all
  const [sortBy, setSortBy] = useState<'pnl' | 'ticker' | 'date' | 'score'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    apiClient.get('/api/discover/pulse-eval')
      .then((r) => setData(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    apiClient.post('/api/discover/pulse-eval/refresh-prices').catch(() => {});
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      apiClient.get('/api/discover/pulse-eval')
        .then((r) => setData(r.data))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Unique tickers for filter dropdown
  const allTickers = useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    [...data.open_positions, ...data.recent_closed].forEach((t) => set.add(t.ticker));
    return Array.from(set).sort();
  }, [data]);

  const handleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const applyFilters = (trades: any[]) => {
    let filtered = trades;
    if (tickerFilter) filtered = filtered.filter((t) => t.ticker === tickerFilter);
    if (sentimentFilter) filtered = filtered.filter((t) => t.sentiment === sentimentFilter);
    if (resultFilter === 'win') filtered = filtered.filter((t) => parseFloat(t.realized_pnl || t.live_pnl || '0') > 0);
    if (resultFilter === 'loss') filtered = filtered.filter((t) => parseFloat(t.realized_pnl || t.live_pnl || '0') < 0);
    return filtered;
  };

  const applySorting = (trades: any[]) => {
    const mul = sortDir === 'desc' ? -1 : 1;
    return [...trades].sort((a, b) => {
      if (sortBy === 'pnl') return mul * (parseFloat(a.realized_pnl || a.live_pnl || '0') - parseFloat(b.realized_pnl || b.live_pnl || '0'));
      if (sortBy === 'ticker') return mul * (a.ticker || '').localeCompare(b.ticker || '');
      if (sortBy === 'score') return mul * ((a.score || 0) - (b.score || 0));
      // date
      return mul * ((a.entry_at || '').localeCompare(b.entry_at || ''));
    });
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const s = data.summary;
  const filteredPending = applyFilters(data.pending_positions || []);
  const filteredOpen = applySorting(applyFilters(data.open_positions));
  const filteredClosed = applySorting(applyFilters(data.recent_closed));
  void (data.cancelled_positions); // available for future cancelled section

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Pulse Signal Evaluation</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Automated paper trades for pulse recommendations (score 7+). Tracks live P&L and evaluates accuracy at expiry.
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {[
          { label: 'Pending', value: s.pending_trades || 0 },
          { label: 'Open', value: s.open_trades },
          { label: 'Closed', value: s.total_trades },
          { label: 'Cancelled', value: s.cancelled_trades || 0 },
          { label: 'Win Rate', value: s.win_rate != null ? `${s.win_rate}%` : '—', color: wrColor(s.win_rate) },
          { label: 'Total P&L', value: pnlStr(s.total_pnl), color: pnlColor(s.total_pnl) },
          { label: 'Avg P&L', value: pnlStr(s.avg_pnl), color: pnlColor(s.avg_pnl) },
          { label: 'Best', value: pnlStr(s.best_trade), color: 'success.main' },
          { label: 'Worst', value: pnlStr(s.worst_trade), color: 'error.main' },
        ].map((card) => (
          <Paper key={card.label} variant="outlined" sx={{ p: 1.5, minWidth: 90, textAlign: 'center', flex: '1 1 auto' }}>
            <Typography variant="caption" color="text.secondary">{card.label}</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: card.color || 'text.primary' }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Ticker</InputLabel>
          <Select value={tickerFilter} label="Ticker" onChange={(e) => setTickerFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {allTickers.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Sentiment</InputLabel>
          <Select value={sentimentFilter} label="Sentiment" onChange={(e) => setSentimentFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="bullish">Bullish</MenuItem>
            <MenuItem value="bearish">Bearish</MenuItem>
            <MenuItem value="neutral">Neutral</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Result</InputLabel>
          <Select value={resultFilter} label="Result" onChange={(e) => setResultFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="win">Winners</MenuItem>
            <MenuItem value="loss">Losers</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredPending.length + filteredOpen.length + filteredClosed.length} trades shown
        </Typography>
      </Box>

      {/* Score Buckets */}
      {s.total_trades > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Accuracy by Conviction Score</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell>Score Range</TableCell>
                  <TableCell align="right">Trades</TableCell>
                  <TableCell align="right">Win Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(data.score_buckets).map(([range, b]) => (
                  <TableRow key={range}>
                    <TableCell><Typography fontWeight={600}>{range}</Typography></TableCell>
                    <TableCell align="right">{b.count}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 600, color: wrColor(b.win_rate) }}>
                        {b.win_rate != null ? `${b.win_rate}%` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Pending — waiting for entry zone */}
      {filteredPending.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Pending Entry ({filteredPending.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Strategy</TableCell>
                  <TableCell align="right">Score</TableCell>
                  <TableCell>Sentiment</TableCell>
                  <TableCell>Signal Date</TableCell>
                  <TableCell>Entry Zone</TableCell>
                  <TableCell align="right">Live Spot</TableCell>
                  <TableCell>Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPending.map((t: any) => (
                  <TableRow key={t.id} hover sx={{ opacity: 0.8 }}>
                    <TableCell><Typography fontWeight={600}>{t.ticker}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{t.strategy_name?.replace(/_/g, ' ')}</Typography></TableCell>
                    <TableCell align="right">{t.score}</TableCell>
                    <TableCell>
                      <Chip label={t.sentiment} size="small"
                        color={t.sentiment === 'bullish' ? 'success' : t.sentiment === 'bearish' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(t.pulse_date)}</Typography></TableCell>
                    <TableCell>
                      {t.entry_zone_low && t.entry_zone_high
                        ? `$${parseFloat(t.entry_zone_low).toFixed(0)}–$${parseFloat(t.entry_zone_high).toFixed(0)}`
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{t.live_spot ? `$${parseFloat(t.live_spot).toFixed(2)}` : '—'}</TableCell>
                    <TableCell>{fmtDate(t.expiry)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Open Positions */}
      {filteredOpen.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Open Positions ({filteredOpen.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell sortDirection={sortBy === 'ticker' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'ticker'} direction={sortBy === 'ticker' ? sortDir : 'asc'} onClick={() => handleSort('ticker')}>Ticker</TableSortLabel>
                  </TableCell>
                  <TableCell>Strategy</TableCell>
                  <TableCell>Legs</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell sortDirection={sortBy === 'score' ? sortDir : false} align="right">
                    <TableSortLabel active={sortBy === 'score'} direction={sortBy === 'score' ? sortDir : 'desc'} onClick={() => handleSort('score')}>Score</TableSortLabel>
                  </TableCell>
                  <TableCell>Sentiment</TableCell>
                  <TableCell sortDirection={sortBy === 'date' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'date'} direction={sortBy === 'date' ? sortDir : 'desc'} onClick={() => handleSort('date')}>Entered</TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Entry $</TableCell>
                  <TableCell align="right">Live $</TableCell>
                  <TableCell sortDirection={sortBy === 'pnl' ? sortDir : false} align="right">
                    <TableSortLabel active={sortBy === 'pnl'} direction={sortBy === 'pnl' ? sortDir : 'desc'} onClick={() => handleSort('pnl')}>P&L</TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Spot %</TableCell>
                  <TableCell align="right">Max P&L</TableCell>
                  <TableCell align="right">DD</TableCell>
                  <TableCell>Target/Stop</TableCell>
                  <TableCell>Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOpen.map((t: any) => {
                  const pnl = parseFloat(t.live_pnl || '0');
                  const sm = spotMove(t.entry_spot, t.live_spot);
                  return (
                    <TableRow key={t.id} hover>
                      <TableCell><Typography fontWeight={600}>{t.ticker}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{t.strategy_name?.replace(/_/g, ' ')}</Typography></TableCell>
                      <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{legsSummary(t.legs)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption">{t.contracts ?? 1}</Typography></TableCell>
                      <TableCell align="right">{t.score}</TableCell>
                      <TableCell>
                        <Chip label={t.sentiment} size="small"
                          color={t.sentiment === 'bullish' ? 'success' : t.sentiment === 'bearish' ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell><Typography variant="caption">{fmtTs(t.entry_at)}</Typography></TableCell>
                      <TableCell align="right">${parseFloat(t.entry_price || '0').toFixed(2)}</TableCell>
                      <TableCell align="right">{t.live_price ? `$${parseFloat(t.live_price).toFixed(2)}` : '—'}</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 600, color: pnlColor(pnl) }}>{pnlStr(pnl)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {sm !== null ? <Typography variant="caption" sx={{ color: pnlColor(sm) }}>{sm >= 0 ? '+' : ''}{sm.toFixed(1)}%</Typography> : '—'}
                      </TableCell>
                      <TableCell align="right">{pnlStr(parseFloat(t.max_profit || '0'))}</TableCell>
                      <TableCell align="right">${parseFloat(t.max_drawdown || '0').toFixed(0)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {t.target_hit_at ? 'T hit' : t.target ? `T:$${parseFloat(t.target).toFixed(0)}` : ''}
                          {(t.target_hit_at || t.target) && (t.stop_hit_at || t.stop) ? ' / ' : ''}
                          {t.stop_hit_at ? 'S hit' : t.stop ? `S:$${parseFloat(t.stop).toFixed(0)}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{fmtDate(t.expiry)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Closed Trades */}
      {filteredClosed.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Closed Trades ({filteredClosed.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  <TableCell sortDirection={sortBy === 'ticker' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'ticker'} direction={sortBy === 'ticker' ? sortDir : 'asc'} onClick={() => handleSort('ticker')}>Ticker</TableSortLabel>
                  </TableCell>
                  <TableCell>Strategy</TableCell>
                  <TableCell>Legs</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell sortDirection={sortBy === 'score' ? sortDir : false} align="right">
                    <TableSortLabel active={sortBy === 'score'} direction={sortBy === 'score' ? sortDir : 'desc'} onClick={() => handleSort('score')}>Score</TableSortLabel>
                  </TableCell>
                  <TableCell>Sentiment</TableCell>
                  <TableCell sortDirection={sortBy === 'date' ? sortDir : false}>
                    <TableSortLabel active={sortBy === 'date'} direction={sortBy === 'date' ? sortDir : 'desc'} onClick={() => handleSort('date')}>Entered</TableSortLabel>
                  </TableCell>
                  <TableCell>Closed</TableCell>
                  <TableCell align="right">Entry $</TableCell>
                  <TableCell align="right">Exit $</TableCell>
                  <TableCell sortDirection={sortBy === 'pnl' ? sortDir : false} align="right">
                    <TableSortLabel active={sortBy === 'pnl'} direction={sortBy === 'pnl' ? sortDir : 'desc'} onClick={() => handleSort('pnl')}>P&L</TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Max DD</TableCell>
                  <TableCell>Target/Stop</TableCell>
                  <TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClosed.map((t: any, i: number) => {
                  const pnl = parseFloat(t.realized_pnl || '0');
                  return (
                    <TableRow key={i} hover>
                      <TableCell><Typography fontWeight={600}>{t.ticker}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{t.strategy_name?.replace(/_/g, ' ')}</Typography></TableCell>
                      <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{legsSummary(t.legs)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption">{t.contracts ?? 1}</Typography></TableCell>
                      <TableCell align="right">{t.score}</TableCell>
                      <TableCell>
                        <Chip label={t.sentiment} size="small"
                          color={t.sentiment === 'bullish' ? 'success' : t.sentiment === 'bearish' ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell><Typography variant="caption">{fmtTs(t.entry_at)}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{fmtTs(t.exit_at)}</Typography></TableCell>
                      <TableCell align="right">${parseFloat(t.entry_price || '0').toFixed(2)}</TableCell>
                      <TableCell align="right">{t.exit_price ? `$${parseFloat(t.exit_price).toFixed(2)}` : '—'}</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 600, color: pnlColor(pnl) }}>{pnlStr(pnl)}</Typography>
                      </TableCell>
                      <TableCell align="right">${parseFloat(t.max_drawdown || '0').toFixed(0)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {t.target_hit_at ? 'T hit' : ''}
                          {t.target_hit_at && t.stop_hit_at ? ' / ' : ''}
                          {t.stop_hit_at ? 'S hit' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={pnl > 0 ? 'Win' : pnl < 0 ? 'Loss' : 'Flat'} size="small"
                          color={pnl > 0 ? 'success' : pnl < 0 ? 'error' : 'default'} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {s.total_trades === 0 && data.open_positions.length === 0 && (
        <Alert severity="info">
          No pulse paper trades yet. Trades auto-open after each pulse computation for tickers with conviction score 7+.
          Check back after the next market hours pulse run.
        </Alert>
      )}
    </Box>
  );
};

export default PulseEval;
